import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  Divider,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Work as WorkIcon,
  Code as CodeIcon,
  Payments as PaymentsIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  HealthAndSafety as HealthAndSafetyIcon
} from '@mui/icons-material';
import { accentBorderCard, designTokens } from '../../theme/designTokens';
import { PREFECTURES, parseLocation, buildLocation } from '../../constants/prefectures';
import { text as textColors } from '../../utils/darkModeAdapter';

const theme = designTokens.featureThemes.job_posting;

const StyledCard = styled(Card)(({ theme: muiTheme }) => ({
  width: '100%',
  ...accentBorderCard('job_posting', muiTheme),
  paddingLeft: muiTheme.spacing(1),
}));

const CardTitle = styled(Typography)(({ theme: muiTheme }) => ({
  fontWeight: 600,
  marginBottom: muiTheme.spacing(2),
  color: theme.primary,
  display: 'flex',
  alignItems: 'center',
}));

const FormField = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
}));

const FieldLabel = styled(Typography)(({ theme: muiTheme }) => ({
  fontWeight: 600,
  fontSize: '0.9rem',
  color: textColors(muiTheme).secondary,
  marginBottom: muiTheme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: muiTheme.spacing(1),
    color: theme.primary
  }
}));

const ButtonsContainer = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(3),
  justifyContent: 'flex-end',
}));

const StyledButton = styled(Button)(({ theme, color }) => ({
  borderRadius: '20px',
  padding: theme.spacing(1, 3),
  boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9rem',
}));

const EditRequirementsCard = ({ requirements, onUpdate, onCancel }) => {
  const [editedRequirements, setEditedRequirements] = useState({ ...requirements });

  const handleChange = (field) => (event) => {
    setEditedRequirements({
      ...editedRequirements,
      [field]: event.target.value
    });
  };

  const handleSave = () => {
    onUpdate(editedRequirements);
  };

  return (
    <StyledCard>
      <CardContent>
        <CardTitle variant="h6">
          求人要件を編集
        </CardTitle>
        
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormField>
              <FieldLabel>
                <WorkIcon fontSize="small" />
                募集職種
              </FieldLabel>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={editedRequirements.role || ''}
                onChange={handleChange('role')}
                placeholder="例: フロントエンドエンジニア"
                InputProps={{
                  sx: { 
                    borderRadius: 1.5,
                    marginLeft: '28px',
                    width: 'calc(100% - 28px)'
                  }
                }}
              />
            </FormField>
          </Grid>
          
          <Grid item xs={12}>
            <FormField>
              <FieldLabel>
                <CodeIcon fontSize="small" />
                必要なスキル・経験
              </FieldLabel>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={editedRequirements.skills || ''}
                onChange={handleChange('skills')}
                placeholder="例: React, TypeScript, Redux (カンマ区切りで入力)"
                InputProps={{
                  sx: { 
                    borderRadius: 1.5,
                    marginLeft: '28px',
                    width: 'calc(100% - 28px)'
                  }
                }}
              />
            </FormField>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormField>
              <FieldLabel>
                <PaymentsIcon fontSize="small" />
                給与
              </FieldLabel>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={editedRequirements.salary || ''}
                onChange={handleChange('salary')}
                placeholder="例: 年収500万円〜800万円"
                helperText="※ 半角数字で入力してください"
                InputProps={{
                  sx: { 
                    borderRadius: 1.5,
                    marginLeft: '28px',
                    width: 'calc(100% - 28px)'
                  }
                }}
              />
            </FormField>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormField>
              <FieldLabel>
                <LocationOnIcon fontSize="small" />
                都道府県
              </FieldLabel>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{ marginLeft: '28px', width: 'calc(100% - 28px)' }}
              >
                <Select
                  value={parseLocation(editedRequirements.location).prefecture}
                  onChange={(e) => {
                    const { detail } = parseLocation(editedRequirements.location);
                    setEditedRequirements({
                      ...editedRequirements,
                      location: buildLocation(e.target.value, detail)
                    });
                  }}
                  displayEmpty
                  sx={{ borderRadius: 1.5 }}
                >
                  <MenuItem value="">選択してください</MenuItem>
                  {PREFECTURES.map((pref) => (
                    <MenuItem key={pref} value={pref}>{pref}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FormField>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormField>
              <FieldLabel>
                <LocationOnIcon fontSize="small" />
                補足
              </FieldLabel>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={parseLocation(editedRequirements.location).detail}
                onChange={(e) => {
                  const { prefecture } = parseLocation(editedRequirements.location);
                  setEditedRequirements({
                    ...editedRequirements,
                    location: buildLocation(prefecture, e.target.value)
                  });
                }}
                placeholder="例: 渋谷区（リモート可）"
                InputProps={{
                  sx: {
                    borderRadius: 1.5,
                    marginLeft: '28px',
                    width: 'calc(100% - 28px)'
                  }
                }}
              />
            </FormField>
          </Grid>
          
          <Grid item xs={12}>
            <FormField>
              <FieldLabel>
                <BusinessIcon fontSize="small" />
                会社PR
              </FieldLabel>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                multiline
                rows={3}
                value={editedRequirements.companyPr || ''}
                onChange={handleChange('companyPr')}
                placeholder="会社の魅力やアピールポイントを入力してください"
                InputProps={{
                  sx: { 
                    borderRadius: 1.5,
                    marginLeft: '28px',
                    width: 'calc(100% - 28px)'
                  }
                }}
              />
            </FormField>
          </Grid>
          
          <Grid item xs={12}>
            <FormField>
              <FieldLabel>
                <HealthAndSafetyIcon fontSize="small" />
                福利厚生
              </FieldLabel>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                multiline
                rows={3}
                value={editedRequirements.welfare || ''}
                onChange={handleChange('welfare')}
                placeholder="福利厚生や待遇面の特徴を入力してください"
                InputProps={{
                  sx: { 
                    borderRadius: 1.5,
                    marginLeft: '28px',
                    width: 'calc(100% - 28px)'
                  }
                }}
              />
            </FormField>
          </Grid>
        </Grid>

        <ButtonsContainer direction="row" spacing={2} sx={{ mt: 4 }}>
          <StyledButton 
            variant="outlined" 
            color="inherit"
            onClick={onCancel}
            startIcon={<CancelIcon />}
          >
            キャンセル
          </StyledButton>
          <StyledButton 
            variant="contained" 
            color="primary"
            onClick={handleSave}
            startIcon={<SaveIcon />}
            sx={{
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0, #1976d2)'
              }
            }}
          >
            更新して再生成
          </StyledButton>
        </ButtonsContainer>
      </CardContent>
    </StyledCard>
  );
};

export default EditRequirementsCard; 